import { Observable } from 'rxjs';

import { Inject, Injectable } from '@angular/core';

import { FsMapMarkersServiceProvider, IFsMap } from '@lib/geo/core';
import { SnLonLat } from '@lib/geo/helpers';
import {
  IMarker,
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerOptions,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from '@lib/geo/map/interfaces';
import { FsMap } from '@lib/geo/map/types';
import { SnBadgeConfig } from '@lib/ui/badge';

import { MapMarkerIcon } from '../core/marker/icon-marker-options';
import { IMapMarkerStyleOptions } from '../core/marker/marker-options';
import { FsMapPopup } from '../core/popup';

@Injectable()
export class MapMarkersFacadeService<TMap extends IFsMap, TMarker extends IMarker<TMap> = IMarker<TMap>> extends FsMapMarkersServiceProvider<TMap, TMarker> {
  constructor(
    @Inject('MAP_MARKERS_PROVIDER') private readonly _provider: FsMapMarkersServiceProvider<TMap, TMarker>,
  ) {
    super();
  }

  get geospatialProvider(): string {
    return this._provider.geospatialProvider;
  }

  get id(): string {
    return this._provider.id;
  }

  setMap(map: TMap): void {
    this._provider.setMap(map);
  }

  // Core marker operations
  addMarker(marker: IMarkerOptions): TMarker {
    return this._provider.addMarker(marker) as TMarker;
  }

  removeMarker(marker: TMarker): void {
    this._provider.removeMarker(marker);
  }

  updateMarker(marker: TMarker, options: Partial<IMarkerOptions>): void {
    this._provider.updateMarker(marker, options);
  }

  // Marker lifecycle and state management
  setVisible(marker: TMarker, visible: boolean): void {
    this._provider.setVisible(marker, visible);
  }

  setPosition(marker: TMarker, position: SnLonLat): void {
    this._provider.setPosition(marker, position);
  }

  setIcon(marker: TMarker, icon: MapMarkerIcon): void {
    this._provider.setIcon(marker, icon);
  }

  setStyle(marker: TMarker, style: IMapMarkerStyleOptions): void {
    this._provider.setStyle(marker, style);
  }

  setDraggable(marker: TMarker, draggable: boolean): void {
    this._provider.setDraggable(marker, draggable);
  }

  setRotation(marker: TMarker, rotation: number): void {
    this._provider.setRotation(marker, rotation);
  }

  setTitle(marker: TMarker, title: string): void {
    this._provider.setTitle(marker, title);
  }

  // Marker properties and data access
  getMarkerId(marker: TMarker): string {
    return this._provider.getMarkerId(marker);
  }

  getMarkerPosition(marker: TMarker): SnLonLat {
    return this._provider.getMarkerPosition(marker);
  }

  getMarkerTitle(marker: TMarker): string {
    return this._provider.getMarkerTitle(marker);
  }

  getMarkerAttributes(marker: TMarker): Record<string, unknown> {
    return this._provider.getMarkerAttributes(marker);
  }

  getMarkerCenter(marker: TMarker): { center: number[] } {
    return this._provider.getMarkerCenter(marker);
  }

  // Badge management
  setBadge(marker: TMarker, badge: SnBadgeConfig): void {
    this._provider.setBadge(marker, badge);
  }

  getBadge(marker: TMarker): SnBadgeConfig {
    return this._provider.getBadge(marker);
  }

  createBadge(marker: TMarker, badgeClass?: string): void {
    this._provider.createBadge(marker, badgeClass);
  }

  createSelectedBadge(marker: TMarker): void {
    this._provider.createSelectedBadge(marker);
  }

  removeSelectedBadge(marker: TMarker): void {
    this._provider.removeSelectedBadge(marker);
  }

  setBadgeStatus(marker: TMarker, status: string, badgePosition: Record<string, unknown>): void {
    this._provider.setBadgeStatus(marker, status, badgePosition);
  }

  // Popup management
  setPopup(marker: TMarker, popup: FsMapPopup<TMap>): void {
    this._provider.setPopup(marker, popup);
  }

  // Event handling - Mouse and Touch Events
  onMarkerClick(marker: TMarker): Observable<IMarkerClickEvent<TMap>> {
    return this._provider.onMarkerClick(marker);
  }

  onMarkerDoubleClick(marker: TMarker): Observable<IMarkerDoubleClickEvent<TMap>> {
    return this._provider.onMarkerDoubleClick(marker);
  }

  onMarkerRightClick(marker: TMarker): Observable<IMarkerRightClickEvent<TMap>> {
    return this._provider.onMarkerRightClick(marker);
  }

  onMarkerMouseOver(marker: TMarker): Observable<IMarkerMouseOverEvent<TMap>> {
    return this._provider.onMarkerMouseOver(marker);
  }

  onMarkerMouseOut(marker: TMarker): Observable<IMarkerMouseOutEvent<TMap>> {
    return this._provider.onMarkerMouseOut(marker);
  }

  onMarkerMouseEnter(marker: TMarker): Observable<IMarkerMouseEnterEvent<TMap>> {
    return this._provider.onMarkerMouseEnter(marker);
  }

  onMarkerMouseLeave(marker: TMarker): Observable<IMarkerMouseLeaveEvent<TMap>> {
    return this._provider.onMarkerMouseLeave(marker);
  }

  // Event handling - Drag Events
  onMarkerDragStart(marker: TMarker): Observable<IMarkerDragStartEvent<TMap>> {
    return this._provider.onMarkerDragStart(marker);
  }

  onMarkerDrag(marker: TMarker): Observable<IMarkerDragMoveEvent<TMap>> {
    return this._provider.onMarkerDrag(marker);
  }

  onMarkerDragEnd(marker: TMarker): Observable<IMarkerDragEndEvent<TMap>> {
    return this._provider.onMarkerDragEnd(marker);
  }

  // Event handling - State Change Events
  onMarkerPositionChange(marker: TMarker): Observable<IMarkerPositionChangeEvent<TMap>> {
    return this._provider.onMarkerPositionChange(marker);
  }

  onMarkerTitleChange(marker: TMarker): Observable<IMarkerTitleChangeEvent<TMap>> {
    return this._provider.onMarkerTitleChange(marker);
  }

  onMarkerVisibilityChange(marker: TMarker): Observable<IMarkerVisibilityChangeEvent<TMap>> {
    return this._provider.onMarkerVisibilityChange(marker);
  }

  onMarkerSelectionChange(marker: TMarker): Observable<IMarkerSelectionChangeEvent<TMap>> {
    return this._provider.onMarkerSelectionChange(marker);
  }

  onMarkerIconChange(marker: TMarker): Observable<IMarkerIconChangeEvent<TMap>> {
    return this._provider.onMarkerIconChange(marker);
  }

  onMarkerStyleChange(marker: TMarker): Observable<IMarkerStyleChangeEvent<TMap>> {
    return this._provider.onMarkerStyleChange(marker);
  }

  // Event handling - Lifecycle Events
  onMarkerAdd(marker: TMarker): Observable<IMarkerAddEvent<TMap>> {
    return this._provider.onMarkerAdd(marker);
  }

  onMarkerRemove(marker: TMarker): Observable<IMarkerRemoveEvent<TMap>> {
    return this._provider.onMarkerRemove(marker);
  }

  onMarkerDestroy(marker: TMarker): Observable<IMarkerDestroyEvent<TMap>> {
    return this._provider.onMarkerDestroy(marker);
  }

  // Event handling - All Events (for general event listening)
  onMarkerEvents(marker: TMarker): Observable<IMarkerEventUnion<TMap>> {
    return this._provider.onMarkerEvents(marker);
  }

  // Marker query and search
  findMarkerById(id: string): TMarker | null {
    return this._provider.findMarkerById(id) as TMarker;
  }

  getAllMarkers(): TMarker[] {
    return this._provider.getAllMarkers() as TMarker[];
  }

  getMarkersByAttribute(attribute: string, value: unknown): TMarker[] {
    return this._provider.getMarkersByAttribute(attribute, value) as TMarker[];
  }
}
